import { ChangeDetectorRef, Component, OnDestroy, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { MatIconButton, MatButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';

export interface AiPdfUploadDialogOptions {
    title: string;
    text: string;
    fileUrl?: string;
    fileName?: string;
}

@Component({
    selector: 'app-ai-pdf-upload-dialog',
    templateUrl: './ai-pdf-upload-dialog.component.html',
    styleUrls: ['./ai-pdf-upload-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatDialogTitle, CdkScrollable, MatDialogContent, MatProgressSpinner, MatIcon, RxReactiveFormsModule, MatIconButton, MatDialogActions, MatButton, MatDialogClose, TranslatePipe]
})
export class AiPdfUploadDialog implements OnDestroy {
    dialogRef = inject<MatDialogRef<AiPdfUploadDialog>>(MatDialogRef);
    data = inject<AiPdfUploadDialogOptions>(MAT_DIALOG_DATA);
    private http = inject(HttpClient);
    private sanitizer = inject(DomSanitizer);
    private cd = inject(ChangeDetectorRef);

    activeTab: 'server' | 'upload' = 'server';
    file: File | null = null;
    filePreviewUrl: SafeResourceUrl | null = null;
    loading = false;
    dragOver = false;
    error: string | null = null;
    private objectUrl: string | null = null;

    get hasServerFile(): boolean {
        return !!this.data.fileUrl;
    }

    get canSubmit(): boolean {
        if (this.activeTab === 'server') return this.hasServerFile;
        return !!this.file;
    }

    constructor() {
        this.activeTab = this.hasServerFile ? 'server' : 'upload';
    }

    ngOnDestroy() {
        this.revokePreview();
    }

    private setPreview(file: File) {
        this.revokePreview();
        this.objectUrl = URL.createObjectURL(file);
        this.filePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    }

    private revokePreview() {
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
            this.filePreviewUrl = null;
        }
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = true;
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = false;
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = false;
        const file = event.dataTransfer?.files[0];
        if (file && file.type === 'application/pdf') {
            this.file = file;
            this.setPreview(file);
            this.error = null;
        }
    }

    onFileSelect(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) {
            this.file = file;
            this.setPreview(file);
            this.error = null;
        }
    }

    removeFile() {
        this.file = null;
        this.revokePreview();
    }

    async submit() {
        this.loading = true;
        this.error = null;

        try {
            const token = localStorage.getItem(`${environment.slug}-jwt`);
            const headers: Record<string, string> = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;

            let response: any;

            if (this.activeTab === 'server' && this.data.fileUrl) {
                response = await firstValueFrom(
                    this.http.post(`${environment.api.url}/ai/prefill-texts`, { file_url: this.data.fileUrl }, { headers })
                );
            } else if (this.file) {
                const formData = new FormData();
                formData.append('file', this.file);
                response = await firstValueFrom(
                    this.http.post(`${environment.api.url}/ai/prefill-texts`, formData, { headers })
                );
            }

            this.dialogRef.close(response);
        } catch (err) {
            this.error = 'PRODUCTION_TEXTS.AI_ERROR';
            this.loading = false;
            // HttpClient errors do not make Angular check the views without zone.js
            this.cd.markForCheck();
        }
    }
}
